const ROTA_API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";
const ROUTE_HISTORY_KEY = "vigial_route_history_v1";

function loadRouteHistory() {
  try { return JSON.parse(localStorage.getItem(ROUTE_HISTORY_KEY) || "[]"); } catch (e) { return []; }
}
function saveRouteHistory(list) {
  try { localStorage.setItem(ROUTE_HISTORY_KEY, JSON.stringify(list)); } catch (e) {}
}

function catLabelFor(lang, catId) {
  const cat = (window.CATS || []).find((c) => c.id === catId);
  return cat ? t(lang, cat.key) : catId;
}

function RouteMap({ lang, coordinates, occurrences, color }) {
  const mapDivRef = React.useRef(null);

  React.useEffect(() => {
    if (!coordinates || !coordinates.length || !mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: false, scrollWheelZoom: true, touchZoom: true, attributionControl: false });
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    const latlngs = coordinates.map((c) => [c[1], c[0]]);
    const line = L.polyline(latlngs, { color: color, weight: 4, opacity: 0.9 }).addTo(map);

    const originIcon = L.divIcon({
      className: "",
      html: `<div style="width:14px;height:14px;border-radius:50%;background:#3E7A4E;border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>`,
      iconSize: [14, 14], iconAnchor: [7, 7],
    });
    L.marker(latlngs[0], { icon: originIcon }).addTo(map).bindPopup(t(lang, "routeOriginLabel"));

    const destIcon = L.divIcon({
      className: "",
      html: `<div style="width:24px;height:24px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;"><span style="display:inline-block;width:10px;height:10px;transform:rotate(45deg);background-color:#fff;-webkit-mask-image:url('https://unpkg.com/lucide-static@0.462.0/icons/flag.svg');mask-image:url('https://unpkg.com/lucide-static@0.462.0/icons/flag.svg');-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;"></span></div>`,
      iconSize: [24, 24], iconAnchor: [12, 24],
    });
    L.marker(latlngs[latlngs.length - 1], { icon: destIcon }).addTo(map).bindPopup(t(lang, "routeDestLabel"));

    const occBounds = [];
    (occurrences || []).forEach((o) => {
      const cat = o.category || "outro";
      const tone = (cat === "roubo" || cat === "veiculo" || cat === "agressao") ? "var(--color-alert)" : (cat === "furto" || cat === "trafico") ? "var(--color-warning)" : "var(--color-text-secondary)";
      const icon = L.divIcon({ className: "", html: `<div class="vigi-map-pin" style="background:${tone}"></div>`, iconSize: [13, 13], iconAnchor: [7, 7] });
      const marker = L.marker([o.lat, o.lng], { icon }).addTo(map);
      const badge = `<span style="display:inline-block;font-size:10px;font-weight:600;padding:1px 7px;border-radius:999px;background:var(--color-brand-tint);color:var(--color-brand-deep);margin-bottom:4px;">${t(lang, "sourceCommunity")}</span>`;
      marker.bindPopup(`${badge}<br><b>${catLabelFor(lang, cat)}</b><br>${t(lang, "nearThisRoute")}`);
      occBounds.push([o.lat, o.lng]);
    });
    const bounds = L.latLngBounds(latlngs.concat(occBounds));
    map.fitBounds(bounds, { padding: [16, 16] });
    return () => map.remove();
  }, [coordinates]);

  return <div ref={mapDivRef} style={{ height: 140, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />;
}

function RouteCard({ lang, title, badgeColor, route }) {
  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: badgeColor }}>{title}</span>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>
          {route.distanceKm} km · {route.durationMin} min
        </span>
      </div>
      <RouteMap lang={lang} coordinates={route.coordinates} occurrences={route.occurrences} color={badgeColor} />
      <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginTop: 6 }}>
        {route.occurrenceCount === 0
          ? t(lang, "noOccurrencesNearRoute")
          : `${route.occurrenceCount} ${catCountLabel(lang, route.occurrenceCount)}`}
      </div>
    </div>
  );
}

function catCountLabel(lang, count) {
  if (lang === "en") return `occurrence${count > 1 ? "s" : ""} recently near this route.`;
  if (lang === "es") return `ocurrencia${count > 1 ? "s" : ""} reciente${count > 1 ? "s" : ""} cerca de esta ruta.`;
  return `ocorrência${count > 1 ? "s" : ""} recente${count > 1 ? "s" : ""} perto desse trajeto.`;
}

function RotaScreen({ lang, showToast }) {
  const { Button, Icon, Chip } = window.VigiALDesignSystem_ff2dec;
  const [rotaMode, setRotaMode] = React.useState("trace");
  const [origin, setOrigin] = React.useState(null);
  const [locStatus, setLocStatus] = React.useState("locating");
  const [destQuery, setDestQuery] = React.useState("");
  const [selectedDest, setSelectedDest] = React.useState(null);
  const [status, setStatus] = React.useState("idle");
  const [errorMsg, setErrorMsg] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [history, setHistory] = React.useState(loadRouteHistory);

  React.useEffect(() => {
    if (typeof window.isSecureContext !== "undefined" && !window.isSecureContext) { setLocStatus("insecure"); return; }
    if (!navigator.geolocation) { setLocStatus("denied"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocStatus("ok"); },
      () => setLocStatus("denied"),
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  async function handleTraceRoute(override) {
    setErrorMsg("");
    if (!origin) { setErrorMsg(t(lang, "noLocationErr")); return; }
    const queryText = override ? override.name : destQuery;
    if (!queryText.trim()) { setErrorMsg(t(lang, "typeDestErr")); return; }
    setResult(null);
    let dest = null;
    const destName = queryText.trim();
    if (override) {
      dest = { lat: override.lat, lng: override.lng };
    } else if (selectedDest && selectedDest.name === destQuery.trim()) {
      dest = { lat: selectedDest.lat, lng: selectedDest.lng };
    } else {
      setStatus("geocoding");
      try {
        const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(destQuery + ", Maceió, Brasil")}&viewbox=${window.MACEIO_VIEWBOX}&bounded=1`);
        const geoData = await geoRes.json();
        if (!geoData || !geoData[0]) { setStatus("error"); setErrorMsg(t(lang, "destNotFound")); return; }
        dest = { lat: parseFloat(geoData[0].lat), lng: parseFloat(geoData[0].lon) };
      } catch (e) {
        setStatus("error");
        setErrorMsg(t(lang, "routeCalcErr"));
        return;
      }
    }
    try {
      setStatus("routing");
      const routeRes = await fetch(`${ROTA_API_BASE}/route`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId: window.getDeviceId ? window.getDeviceId() : null, originLat: origin.lat, originLng: origin.lng, destLat: dest.lat, destLng: dest.lng, window: "90d" }),
      });
      const routeData = await routeRes.json();
      if (routeData.error) { setStatus("error"); setErrorMsg(routeData.error); return; }
      setResult(routeData);
      setStatus("done");
      const summary = routeData.fastest;
      const entry = { id: "rh" + Date.now(), name: destName, lat: dest.lat, lng: dest.lng, distanceKm: summary.distanceKm, durationMin: summary.durationMin, createdAt: Date.now() };
      setHistory((prev) => {
        const next = [entry, ...prev.filter((h) => Math.abs(h.lat - entry.lat) > 0.0005 || Math.abs(h.lng - entry.lng) > 0.0005)].slice(0, 8);
        saveRouteHistory(next);
        return next;
      });
    } catch (e) {
      setStatus("error");
      setErrorMsg(t(lang, "routeCalcErr"));
    }
  }

  function replayHistoryEntry(h) {
    setDestQuery(h.name);
    setSelectedDest({ name: h.name, lat: h.lat, lng: h.lng });
    handleTraceRoute({ name: h.name, lat: h.lat, lng: h.lng });
  }

  function clearRouteHistory() {
    setHistory([]);
    saveRouteHistory([]);
  }

  return (
    <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Chip active={rotaMode === "trace"} onClick={() => setRotaMode("trace")}>{t(lang, "rotaModeTrace")}</Chip>
        <Chip active={rotaMode === "desloc"} onClick={() => setRotaMode("desloc")}>{t(lang, "rotaModeDeslocamento")}</Chip>
      </div>

      {rotaMode === "desloc" ? (
        <window.DeslocamentoMode lang={lang} showToast={showToast} />
      ) : (
      <>
      <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: 8 }}>{t(lang, "whereAreYouGoing")}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
        <Icon name="locate-fixed" size={13} color={locStatus === "ok" ? "var(--color-brand-primary)" : "var(--color-text-muted)"} />
        {locStatus === "ok" && t(lang, "startingFromLocation")}
        {locStatus === "locating" && t(lang, "locating")}
        {locStatus === "denied" && t(lang, "noLocationAccess")}
        {locStatus === "insecure" && t(lang, "noLocationInsecure")}
      </div>

      <window.PlaceSuggestInput
        value={destQuery}
        onChangeText={(v) => { setDestQuery(v); setSelectedDest(null); }}
        onSelect={(s) => { setDestQuery(s.name); setSelectedDest(s); }}
        placeholder={t(lang, "destPlaceholder")}
        style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
      />

      <Button variant="primary" onClick={() => handleTraceRoute()} disabled={status === "geocoding" || status === "routing"} style={{ width: "100%", marginBottom: 8 }}
        icon={(p) => <Icon name="route" size={15} color="#fff" {...p} />}>
        {status === "geocoding" ? t(lang, "searchingDest") : status === "routing" ? t(lang, "calculatingRoute") : t(lang, "traceRoute")}
      </Button>

      {errorMsg && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 10 }}>{errorMsg}</div>}

      {history.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>{t(lang, "routeHistoryTitle")}</span>
            <button onClick={clearRouteHistory} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-body)" }}>
              {t(lang, "clearRouteHistory")}
            </button>
          </div>
          {history.map((h) => (
            <button
              key={h.id}
              onClick={() => replayHistoryEntry(h)}
              disabled={status === "geocoding" || status === "routing"}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 10px", boxSizing: "border-box",
                border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", marginBottom: 6, background: "transparent",
                cursor: "pointer", fontFamily: "var(--font-body)", textAlign: "left",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <Icon name="history" size={13} color="var(--color-text-muted)" />
                <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</span>
              </span>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", flexShrink: 0 }}>{h.distanceKm} km</span>
            </button>
          ))}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 6 }}>
          {result.sameRoute ? (
            <>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 8 }}>
                {t(lang, "sameRouteMsg")}
              </div>
              <RouteCard lang={lang} title={t(lang, "routeLabel")} badgeColor="var(--color-brand-primary)" route={result.fastest} />
            </>
          ) : (
            <>
              <RouteCard lang={lang} title={t(lang, "fastestLabel")} badgeColor="var(--color-brand-primary)" route={result.fastest} />
              <RouteCard lang={lang} title={t(lang, "safestLabel")} badgeColor="var(--color-alert)" route={result.safest} />
            </>
          )}
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: 2 }}>
            {result.routeCount === 1
              ? t(lang, "onlyOneRouteFound")
              : `${result.routeCount} ${t(lang, "routesCompared")}.`} {t(lang, "routeFootnote")}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
window.RotaScreen = RotaScreen;
